import { useState } from 'react';
import PropTypes from 'prop-types';

import { theme } from '@prose-ui';
import { cx, legacyTheme, styled } from '@prose-ui/legacy';
import { makeStyles } from 'legacyStyles';

import TextField from 'Components/TextField';
import { Typography } from 'Components/Typography';

import { handleKeyPress } from 'utils/a11y';
import { parseLetters } from 'utils/textUtils';

const MonogramContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${legacyTheme.spacing.s24};
`;

const MonogramText = styled(Typography)`
  flex-grow: 1px;

  & span {
    color: ${theme.colors.neutral[800]};
  }
`;

const useStyles = makeStyles({
  monogramTextField: {
    width: 80,
    marginLeft: legacyTheme.spacing.s8,
    paddingBottom: 0,
  },
  monogramTextFieldInput: {
    textAlign: 'center',
  },
});

const MonogramEditor = ({ disabled, monogramsValue, onUpdateMonogram }) => {
  const [error, setError] = useState(null);
  const { classes } = useStyles();

  const saveMonogram = async monogram => {
    setError(null);

    if (monogram !== monogramsValue) {
      onUpdateMonogram(monogram)
        .unwrap()
        .catch(() => setError(monogram));
    }
  };

  const retry = () => saveMonogram(error);

  return (
    <>
      <MonogramContainer>
        <MonogramText htmlFor="monogram" markupName="label" variant="p3">
          Monogram your brush -
          <span> 1-3 uppercase characters, optional. Add to to cart to edit.</span>
        </MonogramText>

        <TextField
          classes={{
            root: classes.monogramTextField,
            component: cx(classes.monogramTextFieldInput, 'hide-text-in-percy'),
          }}
          disabled={disabled}
          id="monogram"
          onChange={async event => {
            // monogram is uppercase 3 digits
            const monogram = parseLetters(event.target.value).substring(0, 3).toUpperCase();
            saveMonogram(monogram);
          }}
          value={monogramsValue}
        />
      </MonogramContainer>

      {error && (
        <Typography color="red" variant="p4">
          Error updating your monogram, please{' '}
          <u onClick={retry} onKeyPress={handleKeyPress(retry)} role="button" tabIndex={0}>
            try again
          </u>
          .
        </Typography>
      )}
    </>
  );
};

MonogramEditor.propTypes = {
  disabled: PropTypes.bool,
  monogramsValue: PropTypes.string.isRequired,
  onUpdateMonogram: PropTypes.func.isRequired,
};

MonogramEditor.defaultProps = {
  disabled: false,
};

export default MonogramEditor;
